import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useCan } from "@/auth/permissions";
import {
  CTRIP_LOOKUPS_QUERY_KEY, ctripLookupsApi, LOOKUP_KIND_LABEL,
  type Candidate, type LookupItem, type LookupKind, type LookupStatus,
} from "@/services/ctripLookups";

/**
 * CTRIP-P1 · 携程对照表（样稿 携程价库直连-样稿.html ④ · tag ctrip-pricelib-mockup-v1）。
 * 上架中心 › 平台上架 › 携程 › 「携程对照表」按钮进入；只列「已勾携程的多日游」行程里用到的城市 / 景点 / 酒店。
 * 候选 = 后端转查携程 QueryCity / QueryPoi / QueryHotelByKeyword；携程报错原话显示，不吞。
 * 契约：API_MAP-携程价库直连.md ④（GET lookups / candidates · PUT 确认 / 携程没有）。
 */

const KINDS: LookupKind[] = ["city", "poi", "hotel"];
const STATUS_LABEL: Record<LookupStatus, string> = { pending: "待对", matched: "已对", not_on_ctrip: "携程没有" };
const COLS: Record<LookupKind, string> = { city: "我们的城市", poi: "我们的景点", hotel: "我们的酒店" };

function errText(e: unknown): string {
  const d = (e as { detail?: { detail?: unknown } })?.detail?.detail;
  if (typeof d === "string" && d) return d;
  return (e as Error)?.message || "请求失败";
}

function CandidateCell({ item, canManage, onPick, onNotFound, busy }: {
  item: LookupItem; canManage: boolean; busy: boolean;
  onPick: (c: Candidate) => void; onNotFound: () => void;
}) {
  const [keyword, setKeyword] = useState<string | undefined>(undefined);   // undefined = 默认关键词（英文名，没有用中文名）
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [picked, setPicked] = useState<number | null>(null);
  const q = useQuery({
    queryKey: [...CTRIP_LOOKUPS_QUERY_KEY, "candidates", item.kind, item.our_id, keyword ?? ""],
    queryFn: () => ctripLookupsApi.candidates(item.kind, item.our_id, keyword),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const list = q.data?.candidates ?? [];
  const current = picked ?? list[0]?.ctrip_id ?? null;   // 默认选第一个
  const chosen = list.find((c) => c.ctrip_id === current);

  return (
    <>
      <td data-testid={`cands-${item.our_id}`}>
        <div className="flex flex-col gap-1">
          {q.isLoading && <span className="text-[12px] text-slate-400">正在向携程搜「{keyword ?? item.name_en ?? item.name_cn}」…</span>}
          {q.isError && <span role="alert" className="text-[12.5px] text-rose-600">携程：{errText(q.error)}</span>}
          {q.isSuccess && list.length === 0 && <span className="text-[12px] text-slate-400">携程没搜到「{q.data.keyword}」</span>}
          {list.map((c) => (
            <label key={c.ctrip_id} className="flex items-baseline gap-1.5 text-[12.5px] text-slate-600">
              <input type="radio" name={`cand-${item.our_id}`} checked={current === c.ctrip_id} onChange={() => setPicked(c.ctrip_id)} aria-label={`${c.name ?? ""} #${c.ctrip_id}`} />
              <span>{c.name}</span>
              <span className="font-mono text-[12px] text-slate-500">#{c.ctrip_id}</span>
              <span className="text-[11.5px] text-slate-400">{c.full_name}</span>
            </label>
          ))}
          {editing ? (
            <span className="flex flex-wrap items-center gap-1.5">
              <input
                value={draft} onChange={(e) => setDraft(e.target.value)} aria-label="换个词再搜" placeholder="换个关键词（英文更准）"
                onKeyDown={(e) => { if (e.key === "Enter" && draft.trim()) { setKeyword(draft.trim()); setPicked(null); setEditing(false); } }}
                className="rounded-md border border-slate-300 px-2 py-1 text-[12.5px]"
              />
              <button type="button" disabled={!draft.trim()} onClick={() => { setKeyword(draft.trim()); setPicked(null); setEditing(false); }} className="rounded-md border border-slate-300 px-2 py-0.5 text-[12px] text-slate-700 disabled:opacity-40">搜</button>
              <button type="button" onClick={() => setEditing(false)} className="text-[12px] text-slate-400 hover:underline">取消</button>
            </span>
          ) : (
            <button type="button" onClick={() => { setDraft(keyword ?? ""); setEditing(true); }} className="w-fit text-[12.5px] font-medium text-blue-600 hover:underline">换个词再搜</button>
          )}
        </div>
      </td>
      <td>
        {canManage ? (
          <div className="flex flex-wrap gap-1.5">
            {chosen && (
              <button type="button" disabled={busy} data-testid={`confirm-${item.our_id}`} onClick={() => onPick(chosen)} className="rounded-md bg-blue-600 px-2.5 py-1 text-[12px] font-medium text-white disabled:opacity-50">确认</button>
            )}
            <button type="button" disabled={busy} data-testid={`notfound-${item.our_id}`} onClick={onNotFound} className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-700 disabled:opacity-50">携程没有</button>
          </div>
        ) : <span className="text-[12px] text-slate-400">无修改权限</span>}
      </td>
    </>
  );
}

function Row({ item, canManage, busy, onSave }: {
  item: LookupItem; canManage: boolean; busy: boolean;
  onSave: (item: LookupItem, body: { ctrip_id: number } | { not_on_ctrip: true }) => void;
}) {
  const [redo, setRedo] = useState(false);   // 已对 / 携程没有 的行点「改」→ 回到选候选
  const searching = item.status === "pending" || redo;
  return (
    <tr className="border-b border-slate-100 align-top" data-testid={`lookup-row-${item.our_id}`}>
      <td className="px-2.5 py-2.5">
        <div className="font-semibold text-slate-900">{item.name_cn}</div>
        {item.name_en && <div className="text-[12px] text-slate-400">{item.name_en}</div>}
      </td>
      <td className="px-2.5 py-2.5 tabular-nums">{item.used_by_count} 个</td>
      {searching ? (
        <CandidateCell
          item={item} canManage={canManage} busy={busy}
          onPick={(c) => { onSave(item, { ctrip_id: c.ctrip_id }); setRedo(false); }}
          onNotFound={() => { onSave(item, { not_on_ctrip: true }); setRedo(false); }}
        />
      ) : (
        <>
          <td className="px-2.5 py-2.5">
            {item.status === "matched"
              ? <span className="text-[12.5px] text-slate-600">已对上携程 <span className="font-mono text-[12px]">#{item.ctrip_id}</span></span>
              : <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[12px] text-slate-600">携程没有</span>}
          </td>
          <td className="px-2.5 py-2.5">
            {canManage && <button type="button" data-testid={`redo-${item.our_id}`} onClick={() => setRedo(true)} className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-700">改</button>}
          </td>
        </>
      )}
    </tr>
  );
}

export function CtripLookupsPage() {
  const qc = useQueryClient();
  const canManage = useCan("channels.ctrip_lookups.manage");
  const [sp, setSp] = useSearchParams();   // ?kind=city|poi|hotel（建档预检的「去对照」链接直达某一类）
  const rawKind = sp.get("kind");
  const kind: LookupKind = rawKind === "city" || rawKind === "hotel" ? rawKind : "poi";
  const setKind = (k: LookupKind) => setSp({ kind: k }, { replace: true });
  const [status, setStatus] = useState<LookupStatus>("pending");
  const [staleNote, setStaleNote] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const summaryQ = useQuery({ queryKey: [...CTRIP_LOOKUPS_QUERY_KEY, "summary"], queryFn: ctripLookupsApi.summary });
  const listQ = useQuery({ queryKey: [...CTRIP_LOOKUPS_QUERY_KEY, "list", kind, status], queryFn: () => ctripLookupsApi.list(kind, status) });
  const countsQ = useQuery({ queryKey: [...CTRIP_LOOKUPS_QUERY_KEY, "list", kind, "all"], queryFn: () => ctripLookupsApi.list(kind) });
  const counts = countsQ.data?.counts ?? listQ.data?.counts;

  const save = useMutation({
    mutationFn: (v: { item: LookupItem; body: { ctrip_id: number } | { not_on_ctrip: true } }) => ctripLookupsApi.save(v.item.kind, v.item.our_id, v.body),
    onSuccess: (res) => {
      setSaveErr(null);
      setStaleNote(res.stale_listings > 0 ? `已改动对照：用到它的 ${res.stale_listings} 个在售携程产品变成「内容待重新提交」。` : null);
      qc.invalidateQueries({ queryKey: CTRIP_LOOKUPS_QUERY_KEY });
    },
    onError: (e) => setSaveErr(errText(e)),
  });

  const tabLabel = (k: LookupKind) => {
    const n = summaryQ.data?.[k].pending;
    return `${LOOKUP_KIND_LABEL[k]} · ${n == null ? "…" : n === 0 ? "全部对好" : `待对 ${n}`}`;
  };

  return (
    <div className="flex flex-col gap-4 p-6 lg:p-8" data-testid="ctrip-lookups-page">
      <header>
        <Link to="/admin/listing" className="mb-1 flex w-fit items-center gap-1.5 text-[13px] font-medium text-blue-600 hover:underline"><ArrowLeft size={14} /> 返回上架中心</Link>
        <h1 className="text-2xl font-bold text-slate-900">携程对照表</h1>
        <span className="text-[13px] text-slate-500">只列「已勾选推送到携程的多日游」行程里用到的城市、景点、酒店；候选是系统按名字去携程搜的，同事点选确认。</span>
      </header>

      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {KINDS.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={kind === k} data-testid={`lookup-tab-${k}`} onClick={() => { setKind(k); setStatus("pending"); }}
            className={`border-b-2 px-4 py-2 text-[13.5px] ${kind === k ? "border-blue-600 font-semibold text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{tabLabel(k)}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="lookup-status-chips">
        {(["pending", "matched", "not_on_ctrip"] as LookupStatus[]).map((s) => (
          <button key={s} type="button" aria-pressed={status === s} onClick={() => setStatus(s)}
            className={`rounded-full border px-3 py-0.5 text-[12.5px] ${status === s ? "border-blue-600 bg-blue-600 font-semibold text-white" : "border-slate-200 bg-white text-slate-600"}`}>
            {STATUS_LABEL[s]} {counts ? counts[s] : ""}
          </button>
        ))}
      </div>

      {staleNote && <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">{staleNote}</div>}
      {saveErr && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">保存失败：{saveErr}</div>}

      {listQ.isLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-[13px] text-slate-500">加载中...</div>
      ) : listQ.isError ? (
        <button type="button" onClick={() => listQ.refetch()} className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700">加载失败，点击重试</button>
      ) : (
        <div className="overflow-x-auto rounded-[10px] border border-slate-200 bg-white">
          <table className="w-full min-w-[820px] text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-[12px] font-medium text-slate-400">
                <th className="px-2.5 py-2">{COLS[kind]}</th><th className="px-2.5 py-2">用到的产品</th>
                <th className="px-2.5 py-2">{status === "pending" ? "携程候选（系统按名字搜的）" : "对照结果"}</th><th className="px-2.5 py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {(listQ.data?.items ?? []).map((it) => (
                <Row key={`${it.our_id}-${it.status}`} item={it} canManage={canManage} busy={save.isPending} onSave={(item, body) => save.mutate({ item, body })} />
              ))}
              {(listQ.data?.items ?? []).length === 0 && (
                <tr><td colSpan={4} className="px-2.5 py-8 text-center text-[13px] text-slate-400">{status === "pending" ? "这一类全部对好了" : "这里还没有"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-[10px] border border-dashed border-slate-300 bg-white px-3 py-2.5 text-[12.5px] text-slate-600">
        <b>「携程没有」怎么处理</b>：景点 → 行程里写成文字活动（不带携程景点 ID）；酒店 → 行程里写「某某酒店或同级」。这样会不会被携程驳回，已问携程（问题 5），以实际审核为准。<br />
        已对好的可以点「改」重选；改了之后，用到它的在售产品会变成「内容待重新提交」。
      </div>
    </div>
  );
}
